// src/components/SuccessPage.tsx

import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';

export const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setError('No session ID provided.');
      return;
    }

    const verifyAndGetDownloadUrl = async () => {
      try {
        const response = await fetch('/.netlify/functions/verify-session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Download failed');
        }

        const data = await response.json();
        setDownloadUrl(data.downloadUrl);

        // Automatically start download
        if (data.downloadUrl) {
          const link = document.createElement('a');
          link.href = data.downloadUrl;
          link.download = data.fileName || 'download.xlam';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

        setStatus('success');
      } catch (err: any) {
        setStatus('error');
        setError(err.message || 'An unexpected error occurred.');
      }
    };

    verifyAndGetDownloadUrl();
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      {status === 'loading' && (
        <div className="text-center">
          <p className="text-xl">Processing your purchase...</p>
        </div>
      )}
      {status === 'success' && (
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-4">Thank you for your purchase!</h2>
          <p className="text-xl mb-6">Your download should start automatically.</p>
          <div className="space-y-3">
            <Link
              to={`/download?session_id=${encodeURIComponent(sessionId || '')}`}
              className="inline-block bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700"
            >
              Download again or get help
            </Link>
            <Link to="/products/excel-colour-picker" className="block text-green-600 hover:underline">
              Return to Product Page
            </Link>
          </div>
        </div>
      )}
      {status === 'error' && (
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-4">Download Failed</h2>
          <p className="text-xl mb-6">{error}</p>
          <Link to={`/products/excel-colour-picker`} className="text-green-600 hover:underline">
            Return to Product Page
          </Link>
        </div>
      )}
    </div>
  );
};
